import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Shield, ShieldCheck, ShieldAlert, Ban, Globe, RefreshCw, Loader2,
  AlertOctagon, AlertTriangle, Info, ArrowRight, Plus, KeyRound, CheckCircle2
} from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, Tooltip, YAxis, XAxis
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADE_STYLES = {
  'A+': { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  'A':  { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  'B':  { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  'C':  { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  'D':  { color: 'text-orange-700',  bg: 'bg-orange-50',  border: 'border-orange-200' },
  'F':  { color: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200' },
};

// ============ SECURITY SCORE CARD ============

const SecurityScoreCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const { toast } = useToast();

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchScore = useCallback(async (spin = true) => {
    if (spin) setLoading(true); else setRefreshing(true);
    try {
      const r = await axios.get(`${API}/api/mybookie/admin/security-score`, authHeaders);
      setData(r.data);
    } catch (err) {
      console.error('SecurityScore fetch failed:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchScore(); }, [fetchScore]);

  if (loading) {
    return (
      <Card data-testid="security-score-loading">
        <CardContent className="flex items-center gap-2 py-6 text-[#5C6661]">
          <Loader2 className="w-4 h-4 animate-spin" /> Computing security score...
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card data-testid="security-score-error">
        <CardContent className="py-6 text-sm text-[#5C6661]">Could not load security score.</CardContent>
      </Card>
    );
  }

  const grade = GRADE_STYLES[data.grade] || GRADE_STYLES.C;
  return (
    <Card className={`border-2 ${grade.border} ${grade.bg}`} data-testid="security-score-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className={`w-5 h-5 ${grade.color}`} /> Security Score
            </CardTitle>
            <CardDescription>Composite of latest daily (60%) + weekly (40%) audits · muted alerts excluded</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="bg-white" onClick={async () => {
              setScanning(true); setScanResult(null);
              try {
                const r = await axios.post(`${API}/api/mybookie/admin/secrets-scan`, {}, authHeaders);
                setScanResult(r.data);
                if (r.data.clean) {
                  toast({ title: 'Clean ✓', description: 'No leaked secrets in source.' });
                } else {
                  toast({
                    title: `${r.data.total} finding${r.data.total === 1 ? '' : 's'}`,
                    description: `${r.data.critical.length} critical · ${r.data.high.length} high · ${r.data.medium.length} medium`,
                    variant: 'destructive',
                  });
                }
              } catch (e) {
                toast({ title: 'Scan failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
              } finally { setScanning(false); }
            }} disabled={scanning} data-testid="secrets-scan-btn">
              {scanning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1.5" />}
              Scan secrets
            </Button>
            <Button variant="outline" size="sm" className="bg-white" onClick={() => fetchScore(false)} disabled={refreshing} data-testid="security-score-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          {/* Score number */}
          <div>
            <div className="flex items-baseline gap-3">
              <div className={`text-6xl font-bold ${grade.color}`} data-testid="security-score-value">
                {data.current_score}
              </div>
              <div className={`text-3xl font-bold ${grade.color}`} data-testid="security-score-grade">
                {data.grade}
              </div>
            </div>
            <div className="text-xs text-[#5C6661] mt-1">Daily: {data.daily_score} · Weekly: {data.weekly_score}</div>
          </div>

          {/* 30-day sparkline */}
          <div className="md:col-span-2">
            <div className="text-xs uppercase text-[#5C6661] mb-1">30-day trend</div>
            {data.history_30d.length > 1 ? (
              <ResponsiveContainer width="100%" height={80}>
                <LineChart data={data.history_30d} margin={{ top: 4, bottom: 4 }}>
                  <YAxis domain={[0, 100]} hide />
                  <XAxis dataKey="date" hide />
                  <Tooltip
                    contentStyle={{ background: 'white', border: '1px solid #E6E8E6', borderRadius: 4, fontSize: 12 }}
                    labelStyle={{ color: '#0A2B1D' }}
                  />
                  <Line type="monotone" dataKey="score" stroke="#0A2B1D" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-xs text-[#5C6661]">Not enough history yet — back in 7 days.</div>
            )}
          </div>
        </div>

        {/* Manual secrets-scan result panel */}
        {scanResult && (
          <div className={`mt-5 pt-4 border-t border-white/40`} data-testid="secrets-scan-result">
            <div className="text-xs uppercase text-[#5C6661] mb-2 flex items-center gap-2">
              <KeyRound className="w-3 h-3" /> Secrets scan · {new Date(scanResult.scanned_at).toLocaleTimeString()}
            </div>
            {scanResult.clean ? (
              <div className="text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> No leaked secrets in source files. All keys are properly using environment variables.
              </div>
            ) : (
              <div className="space-y-1.5">
                {['critical', 'high', 'medium'].map(sev => (
                  (scanResult[sev] || []).slice(0, 5).map((f, idx) => (
                    <div key={`${sev}-${idx}`} className="text-xs text-[#111614] flex items-start gap-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase ${
                        sev === 'critical' ? 'bg-red-100 text-red-800' : sev === 'high' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'
                      }`}>{sev}</span>
                      <code className="text-[11px] bg-[#F6F5F2] px-1 py-0.5 rounded">{f.file}:{f.line}</code>
                      <span className="text-[#5C6661]">— {f.label}</span>
                    </div>
                  ))
                ))}
              </div>
            )}
          </div>
        )}

        {/* Alerts breakdown */}
        {data.alerts_breakdown.length > 0 && (
          <div className="mt-5 pt-4 border-t border-white/40">
            <div className="text-xs uppercase text-[#5C6661] mb-2">Active findings ({data.alerts_breakdown.length}, after mutes)</div>
            <div className="space-y-1">
              {data.alerts_breakdown.slice(0, 4).map((a, idx) => {
                const Icon = a.severity === 'critical' ? AlertOctagon
                  : a.severity === 'high' ? ShieldAlert
                  : a.severity === 'medium' ? AlertTriangle
                  : Info;
                return (
                  <div key={idx} className="text-sm text-[#111614] flex items-center gap-2" data-testid={`security-score-finding-${idx}`}>
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${a.severity === 'critical' ? 'text-red-700' : a.severity === 'high' ? 'text-orange-700' : a.severity === 'medium' ? 'text-amber-700' : 'text-blue-700'}`} />
                    <span className="truncate">{a.message}</span>
                    <span className="ml-auto text-xs text-[#5C6661]">−{a.penalty}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ THREAT MAP CARD ============

const ThreatMapCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [banDialog, setBanDialog] = useState(null);  // { ip, reason }
  const [banning, setBanning] = useState(false);
  const [days, setDays] = useState(30);
  const { toast } = useToast();

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchThreats = useCallback(async (spin = true) => {
    if (spin) setLoading(true); else setRefreshing(true);
    try {
      const r = await axios.get(`${API}/api/mybookie/admin/threat-map?days=${days}&limit=10`, authHeaders);
      setData(r.data);
    } catch (err) {
      console.error('ThreatMap fetch failed:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authHeaders, days]);

  useEffect(() => { fetchThreats(); }, [fetchThreats]);

  const handleBan = async () => {
    if (!banDialog?.ip) return;
    setBanning(true);
    try {
      await axios.post(`${API}/api/mybookie/admin/blocked-ips`,
        { ip: banDialog.ip, reason: banDialog.reason || 'Threat map ban' }, authHeaders);
      toast({ title: 'IP banned', description: `${banDialog.ip} can no longer log in or register.` });
      setBanDialog(null);
      fetchThreats(false);
    } catch (err) {
      toast({ title: 'Ban failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setBanning(false);
    }
  };

  const handleUnban = async (ip) => {
    try {
      await axios.delete(`${API}/api/mybookie/admin/blocked-ips/${ip}`, authHeaders);
      toast({ title: 'IP unbanned', description: `${ip} can log in again.` });
      fetchThreats(false);
    } catch (err) {
      toast({ title: 'Unban failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  if (loading) {
    return <Card><CardContent className="py-6 text-[#5C6661] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Scanning audit log...</CardContent></Card>;
  }

  return (
    <Card data-testid="threat-map-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5 text-[#0A2B1D]" /> Threat Map</CardTitle>
            <CardDescription>Top suspicious IPs from failed logins · last {data?.days || 30} days · banned: {data?.total_banned || 0}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="text-xs px-2 py-1 rounded border border-[#E6E8E6] bg-white"
              data-testid="threat-map-window"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
            <Button variant="outline" size="sm" onClick={() => fetchThreats(false)} disabled={refreshing} data-testid="threat-map-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {(!data?.threats || data.threats.length === 0) ? (
          <div className="text-center py-8 text-[#5C6661] text-sm" data-testid="threat-map-empty">
            <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
            No suspicious activity in the last {data?.days || 30} days. Clean.
          </div>
        ) : (
          <div className="space-y-2" data-testid="threat-map-list">
            {data.threats.map((t, idx) => (
              <div
                key={t.ip}
                className={`flex items-center gap-3 p-3 rounded-md border ${t.banned ? 'bg-red-50 border-red-200' : 'bg-[#F6F5F2] border-[#E6E8E6]'}`}
                data-testid={`threat-row-${idx}`}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-md bg-white flex items-center justify-center">
                  <span className="text-xs font-bold text-[#0A2B1D]">{t.geo.country_code || '?'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-medium text-[#111614]">{t.ip}</span>
                    {t.banned && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">
                        <Ban className="w-3 h-3 mr-0.5" /> BANNED
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#5C6661] mt-0.5">
                    {t.geo.city}{t.geo.region ? `, ${t.geo.region}` : ''} · {t.geo.country}
                    {t.geo.org && <span className="ml-1">· {t.geo.org.substring(0, 40)}</span>}
                  </div>
                  <div className="text-xs text-[#5C6661] mt-0.5">
                    {t.failed_count} failed login{t.failed_count !== 1 ? 's' : ''} · {t.unique_email_count} email{t.unique_email_count !== 1 ? 's' : ''} targeted
                  </div>
                </div>
                {t.banned ? (
                  <Button variant="outline" size="sm" onClick={() => handleUnban(t.ip)} className="bg-white" data-testid={`threat-unban-${idx}`}>
                    Unban
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => setBanDialog({ ip: t.ip, reason: '' })} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid={`threat-ban-${idx}`}>
                    <Ban className="w-3.5 h-3.5 mr-1" /> Ban
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Ban confirmation dialog */}
      <Dialog open={!!banDialog} onOpenChange={(o) => !o && setBanDialog(null)}>
        <DialogContent data-testid="threat-ban-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" /> Ban IP {banDialog?.ip}?
            </DialogTitle>
            <DialogDescription>
              This IP will be blocked from logging in or registering until you unban it. Reasons help future-you remember why.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={banDialog?.reason || ''}
            onChange={(e) => setBanDialog({ ...banDialog, reason: e.target.value })}
            placeholder="Reason (e.g., 'Brute-force attempts on multiple accounts')"
            data-testid="threat-ban-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBanDialog(null)} disabled={banning}>Cancel</Button>
            <Button onClick={handleBan} disabled={banning} className="bg-red-600 hover:bg-red-700 text-white" data-testid="threat-ban-confirm">
              {banning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              {banning ? 'Banning...' : 'Ban this IP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export { SecurityScoreCard, ThreatMapCard };
